import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function BlackScholesCalculator() {
  const [stockPrice, setStockPrice] = useState("");
  const [strikePrice, setStrikePrice] = useState("");
  const [timeToMaturity, setTimeToMaturity] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [volatility, setVolatility] = useState("");
  const [result, setResult] = useState<{ call: number; put: number } | null>(null);

  const calculate = () => {
    const S = parseFloat(stockPrice);
    const K = parseFloat(strikePrice);
    const T = parseFloat(timeToMaturity);
    const r = parseFloat(riskFreeRate) / 100;
    const sigma = parseFloat(volatility) / 100;

    if (isNaN(S) || isNaN(K) || isNaN(T) || isNaN(r) || isNaN(sigma) || S <= 0 || K <= 0 || T <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);
    
    const normCDF = (x: number) => {
      const t = 1 / (1 + 0.2316419 * Math.abs(x));
      const d = 0.3989423 * Math.exp(-x * x / 2);
      const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
      return x > 0 ? 1 - p : p;
    };

    const callPrice = S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);
    const putPrice = K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);

    setResult({ call: callPrice, put: putPrice });
    toast.success("Options priced!");
  };

  const reset = () => {
    setStockPrice("");
    setStrikePrice("");
    setTimeToMaturity("");
    setRiskFreeRate("");
    setVolatility("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Black-Scholes Calculator</CardTitle>
          <CardDescription>European option pricing model</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="stockPrice">Stock Price ($)</Label>
            <Input id="stockPrice" type="number" placeholder="100" value={stockPrice} onChange={(e) => setStockPrice(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="strikePrice">Strike Price ($)</Label>
            <Input id="strikePrice" type="number" placeholder="100" value={strikePrice} onChange={(e) => setStrikePrice(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="timeToMaturity">Time to Maturity (years)</Label>
            <Input id="timeToMaturity" type="number" placeholder="1" value={timeToMaturity} onChange={(e) => setTimeToMaturity(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="riskFreeRate">Risk-Free Rate (%)</Label>
            <Input id="riskFreeRate" type="number" placeholder="5" value={riskFreeRate} onChange={(e) => setRiskFreeRate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="volatility">Volatility (%)</Label>
            <Input id="volatility" type="number" placeholder="20" value={volatility} onChange={(e) => setVolatility(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere"><strong>Call Price:</strong> ${result.call.toFixed(4)}</p>
                <CopyResultButton value={result.call.toFixed(4)} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere"><strong>Put Price:</strong> ${result.put.toFixed(4)}</p>
                <CopyResultButton value={result.put.toFixed(4)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["black-scholes"]} trigger={result?.call} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
